import { orderBy, type CompositeFilterDescriptor, type FilterDescriptor, type SortDescriptor } from '@progress/kendo-data-query';
import { ReactNode, useCallback, useMemo, useState, type CSSProperties } from 'react';
import { queryDashboardFlightsGrid } from '../../../services/grid-query-service';
import { UDViewTagSelectionEnum } from '../../../udView/udview';
import { useAppSelector } from '../../hooks/store-hooks';
import { useSafeEffect } from '../../hooks/use-safe-effect';
import './Dashboard.css';
import DashboardFlightsTable from './DashboardFlightsTable';

export type DashboardFlightRow = Record<string | number, unknown> & { id: string | number };

export type DashboardFlightColumn = {
    id: string;
    field?: string;
    title?: string;
    tagSelector: UDViewTagSelectionEnum;
    justify?: 'right' | 'left';
    sortable?: boolean;
    hidden?: boolean;
    className?: string;
    headerClassName?: string;
    renderCell?: (value: unknown, row: DashboardFlightRow) => ReactNode;
};

export type DashboardCustomColumn = Omit<DashboardFlightColumn, 'tagSelector'>;

export type DashboardFlightsPanelVariant = 'holding' | 'diverting' | 'emergency' | 'go-around' | 'taxi-times' | 'position-updated';

export type DashboardFlightsPanelProps = {
    title: string;
    flightFilters: FilterDescriptor[];
    columns: DashboardFlightColumn[];
    customColumns?: DashboardCustomColumn[];
    defaultSort: SortDescriptor[];
    emptyText: string;
    maxRows?: number;
    variant?: DashboardFlightsPanelVariant;
    includeTaxiTimeMonitorStatus?: boolean;
    includeLastPositionTimeMonitorStatus?: boolean;
    getCellStyle?: (column: DashboardFlightColumn | DashboardCustomColumn, value: unknown, dataItem: any) => CSSProperties | undefined;
};

const EMPTY_CUSTOM_COLUMNS: DashboardCustomColumn[] = [];

export function DashboardFlightsPanel({
    title,
    flightFilters,
    columns,
    defaultSort,
    emptyText,
    maxRows,
    variant = 'holding',
    includeLastPositionTimeMonitorStatus,
    includeTaxiTimeMonitorStatus,
    customColumns = EMPTY_CUSTOM_COLUMNS,
    getCellStyle
}: DashboardFlightsPanelProps) {
    const lastUpdate = useAppSelector((x) => x.global.flightWorker.lastUpdate?.time);
    const [sort, setSort] = useState<SortDescriptor[]>(defaultSort);
    // Custom values exist only in the worker response, so never send their
    // sort descriptors to the worker's raw-flight query.
    const [workerSort, setWorkerSort] = useState<SortDescriptor[]>(() =>
        defaultSort.filter(
            (descriptor) => !customColumns.some((column) => column.id === descriptor.field || column.field === descriptor.field)
        )
    );
    const activeCustomColumn = sort[0]
        ? customColumns.find((column) => column.id === sort[0].field || column.field === sort[0].field)
        : undefined;
    // Fetch the complete matching set before sorting locally and limiting rows.
    const queryTake = activeCustomColumn ? Number.MAX_SAFE_INTEGER : maxRows;
    const [total, setTotal] = useState(0);
    const [data, setData] = useState<DashboardFlightRow[]>([]);
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(0);

    const displayedData = useMemo(() => {
        if (!activeCustomColumn || !sort[0]?.dir) return data;
        const sorted = orderBy(data, [{ field: activeCustomColumn.id, dir: sort[0].dir }]);
        return maxRows == null ? sorted : sorted.slice(0, maxRows);
    }, [data, sort, activeCustomColumn, maxRows]);

    useSafeEffect(() => {
        let cancelled = false;
        setError(false);
        const filter: CompositeFilterDescriptor = {
            logic: 'and',
            filters: flightFilters
        };

        // (ml): Currently we don't make use of Grid virtualization. That is probably fine because due to the filters being so restrictive we're not going to get many flights here
        // but if that were to change we need to enable virtual scrolling on this Grid
        queryDashboardFlightsGrid({
            columns: columns.map((column) => column.tagSelector),
            customColumns: customColumns.map((column) => column.id),
            state: {
                filter,
                sort: workerSort,
                skip: 0,
                take: queryTake
            },
            includeLastPostUpdateMonitorStatus: includeLastPositionTimeMonitorStatus,
            includeTaxiTimeMonitorStatus: includeTaxiTimeMonitorStatus
        })
            .then((result) => {
                if (cancelled) return;
                setTotal(result.total);
                setData(result.data);
            })
            .catch(() => {
                if (cancelled) return;
                setTotal(0);
                setData([]);
                setError(true);
            });

        return () => {
            cancelled = true;
        };
    }, [
        workerSort,
        lastUpdate,
        columns,
        customColumns,
        flightFilters,
        queryTake,
        retry,
        includeLastPositionTimeMonitorStatus,
        includeTaxiTimeMonitorStatus
    ]);

    const sortChanged = useCallback(
        (field: string) => {
            const nextSort: SortDescriptor[] = [
                {
                    field,
                    dir: sort[0]?.field === field && sort[0]?.dir === 'asc' ? 'desc' : 'asc'
                }
            ];
            setSort(nextSort);
            if (!customColumns.some((column) => column.id === field || column.field === field)) {
                setWorkerSort(nextSort);
            }
        },
        [sort, customColumns]
    );
    const retryQuery = useCallback(() => setRetry((current) => current + 1), []);
    const snapshot = useMemo(() => ({ data: displayedData, total, error }), [displayedData, total, error]);

    return (
        <DashboardFlightsTable
            title={title}
            columns={columns}
            customColumns={customColumns}
            emptyText={emptyText}
            variant={variant}
            getCellStyle={getCellStyle}
            snapshot={snapshot}
            sort={sort}
            onSortChange={sortChanged}
            onRetry={retryQuery}
        />
    );
}
